import * as React from 'react';

import {
  Col,
  type DataGridColumn,
  formatMoney,
  Radio,
  RadioGroup,
  cn,
} from '@rematter/ui-web';

import { type QuantityFilter } from '../../../_state/inventoryLevelsList.state';
import { useInventoryUnit } from '../../../hooks/useInventoryUnit';
import { useTranslations } from '../../../i18n/useTranslations';
import { InventoryLevelRecord } from '../../../types/inventoryLevel';

import { getLocale } from '~/shared/utils/locale';

type UseFacilityBreakdownColumnsArgs = {
  quantityFilter: QuantityFilter;
  onQuantityFilterChange: (value: QuantityFilter) => void;
};

export const useFacilityBreakdownColumns = ({
  quantityFilter,
  onQuantityFilterChange,
}: UseFacilityBreakdownColumnsArgs): DataGridColumn<InventoryLevelRecord>[] => {
  const t = useTranslations();
  const { formatQuantityValue } = useInventoryUnit();

  return React.useMemo(
    () => [
      {
        key: 'facility',
        header: t.movementHistoryView.facilityBreakdown.facility,
        cell: ({ row }) => (
          <span className="font-medium">{row.facilityName || '\u2014'}</span>
        ),
      },
      {
        key: 'quantity',
        header: t.movementHistoryView.facilityBreakdown.quantity,
        cell: ({ row }) => (
          <span
            className={cn(
              'tabular-nums',
              row.quantity < 0 && 'font-semibold text-failure',
              row.quantity > 0 && 'font-semibold text-success',
              row.quantity === 0 && 'text-neutral/60',
            )}
          >
            {formatQuantityValue(row.quantity, row.quantityUnit)}
          </span>
        ),
        isFilterActive: quantityFilter !== 'all',
        columnFilter: () => (
          <Col className="p-3">
            <RadioGroup
              value={quantityFilter}
              onChange={(v) => onQuantityFilterChange(v as QuantityFilter)}
            >
              <Radio value="all">
                {t.inventoryLevelsView.filters.allQuantities}
              </Radio>
              <Radio value="hideZero">
                {t.inventoryLevelsView.filters.hideZeroQuantity}
              </Radio>
              <Radio value="negativeOnly">
                {t.inventoryLevelsView.filters.negativeOnly}
              </Radio>
            </RadioGroup>
          </Col>
        ),
      },
      {
        key: 'totalValue',
        header: t.movementHistoryView.facilityBreakdown.totalValue,
        cell: ({ row }) => {
          const cv = row.currencyValues[0];
          if (!cv || cv.totalValue == null) {
            return <span className="text-neutral/40">{'\u2014'}</span>;
          }

          return (
            <span className="tabular-nums">
              {formatMoney(cv.totalValue, cv.currencyCode, getLocale())}
            </span>
          );
        },
      },
      {
        key: 'wac',
        header: t.movementHistoryView.facilityBreakdown.wac,
        cell: ({ row }) => {
          const cv = row.currencyValues[0];
          if (!cv || cv.weightedAverageCost == null) {
            return <span className="text-neutral/40">{'\u2014'}</span>;
          }

          return (
            <span className="tabular-nums">
              {formatMoney(
                cv.weightedAverageCost,
                cv.currencyCode,
                getLocale(),
              )}
            </span>
          );
        },
      },
    ],
    [t, formatQuantityValue, quantityFilter, onQuantityFilterChange],
  );
};
